/**
 * File: Logo.js
 * Origin: isotect
 * Description: Logo component that renders company logos
 *
 * Version: v1.1.0
 * Created: 2025-07-24
 * Last Updated: 2026-04-23
 *
 * Used By: isotect
 * Maintainer: Zachary Dicks
 *
 * Notes:
 * - Changes in this file may affect all consuming frontend modules.
 * - See Git repository 'isotect' for full change history.
 */

// Third-party packages
import { Link } from "react-router-dom";

// Local project files
import logoSmall from "../../assets/logos/Isotect-Logo-250px.png";
import logoMedium from "../../assets/logos/Isotect-Logo-500px.png";
import logoLarge from "../../assets/logos/Isotect-Logo-1000px.png";

/**
 * Logo component that renders different company logo variants.
 *
 * @component
 *
 * @param {Object} props - Component properties
 * @param {string} [props.type] - Type of logo to render
 * @param {boolean} [props.isLink] - Whether the logo should be wrapped in a navigation link
 * @param {string} [props.linkHref] - Destination URL when the logo is a link
 * @param {string} [props.alt] - Alternative text for the logo image
 * @param {string} [props.className] - Additional CSS classes for styling
 *
 * @returns {JSX.Element} Rendered logo component
 */
function Logo({
    type = "small",
    isLink = false,
    linkHref = "/",
    alt = "Isotect Logo",
    className = "",
}) {
    const iconPath = "/favicons/android-chrome-192x192.png";

    const logoConfig = {
        icon: { src: iconPath, classModifier: "logo--icon" },
        small: { src: logoSmall, classModifier: "logo--small" },
        medium: { src: logoMedium, classModifier: "logo--medium" },
        large: { src: logoLarge, classModifier: "logo--large" },
    };

    const config = logoConfig[type] || logoConfig.small;

    const image = (
        <img
            src={config.src}
            alt={alt}
            className={`logo ${config.classModifier} ${className}`}
            loading="lazy"
        />
    );

    return isLink ? <Link to={linkHref}>{image}</Link> : image;
}

export default Logo;
